import { cloneElement, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import catalogMetadata from "../data/catalogPosterMetadata.json";
import { moviePosterMetadata } from "../utils/posterMetadata.mjs";
import MoviePoster from "./MoviePoster";

// Clone the existing poster/card so its dimensions, styling and click action
// stay intact. The portal escapes transformed cards and clipped poster walls.
export default function MoviePosterPreview({ children, movieId, title, urls = [], overview,
  tmdbId, imdbId, year, rating, genres = [] }) {
  const id = useId();
  const anchor = useRef(null);
  const popup = useRef(null);
  const poster = useRef(null);
  const hovered = useRef(false);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const cached = catalogMetadata[movieId]?.canonical_title === title ? catalogMetadata[movieId] : null;
  const synopsis = overview || metadata?.overview || cached?.overview;

  const show = event => {
    anchor.current = event.currentTarget;
    document.dispatchEvent(new CustomEvent("movie-preview-open", { detail: id }));
    if (!open) setPosition(null);
    setOpen(true);
  };

  useEffect(() => {
    if (!open || synopsis || !tmdbId) return;
    let cancelled = false;
    moviePosterMetadata(movieId, title, tmdbId, imdbId)
      .then(value => { if (!cancelled) setMetadata(value); })
      .catch(() => {}); // The preview still works if optional metadata is unavailable.
    return () => { cancelled = true; };
  }, [open, synopsis, movieId, title, tmdbId, imdbId]);

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      if (!anchor.current || !popup.current) return;
      const rect = anchor.current.getBoundingClientRect();
      const viewport = window.visualViewport;
      const left = viewport?.offsetLeft || 0;
      const top = viewport?.offsetTop || 0;
      const width = Math.min(viewport?.width || window.innerWidth, window.innerWidth);
      const height = Math.min(viewport?.height || window.innerHeight, window.innerHeight);
      const margin = 12;
      // Recommendation cards get wider when the panels stack on tablets. Grow
      // the preview with its trigger so the poster is still an enlargement.
      const previewWidth = Math.max(620, Math.ceil(anchor.current.clientWidth * 1.2) * 2 + 50);
      // Apply current bounds before measuring, including the first frame after
      // resizing or reopening a preview that was last used on a wider screen.
      popup.current.style.maxWidth = `${width - margin * 2}px`;
      popup.current.style.maxHeight = `${height - margin * 2}px`;
      popup.current.style.width = `${previewWidth}px`;
      if (poster.current) poster.current.style.maxWidth = `${(previewWidth - 50) / 2}px`;
      const size = popup.current.getBoundingClientRect();
      const right = left + width - margin;
      const bottom = top + height - margin;
      // Overlay the hovered poster, keeping the entire preview in the viewport.
      const x = Math.max(left + margin, Math.min(rect.left + (rect.width - size.width) / 2, right - size.width));
      const y = Math.max(top + margin, Math.min(rect.top + (rect.height - size.height) / 2, bottom - size.height));
      setPosition({ left: x, top: y, width: previewWidth, maxWidth: width - margin * 2, maxHeight: height - margin * 2 });
    };
    const dismiss = event => {
      if (event.type === "keydown" && event.key !== "Escape") return;
      if (event.type !== "keydown" && (anchor.current?.contains(event.target) || popup.current?.contains(event.target))) return;
      setOpen(false);
    };
    const anotherPreview = event => { if (event.detail !== id) setOpen(false); };
    place();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(place);
    if (popup.current) observer?.observe(popup.current);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    window.visualViewport?.addEventListener("resize", place);
    window.visualViewport?.addEventListener("scroll", place);
    document.addEventListener("keydown", dismiss);
    document.addEventListener("pointerdown", dismiss);
    document.addEventListener("movie-preview-open", anotherPreview);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
      window.visualViewport?.removeEventListener("resize", place);
      window.visualViewport?.removeEventListener("scroll", place);
      document.removeEventListener("keydown", dismiss);
      document.removeEventListener("pointerdown", dismiss);
      document.removeEventListener("movie-preview-open", anotherPreview);
    };
  }, [open, id]);

  const previewUrls = [...urls, ...(metadata?.poster_urls || []), ...(cached?.poster_urls || [])];
  return <>
    {cloneElement(children, {
      "data-poster-preview-trigger": movieId,
      "aria-describedby": [children.props["aria-describedby"], open ? id : null].filter(Boolean).join(" ") || undefined,
      onMouseEnter: event => { children.props.onMouseEnter?.(event); hovered.current = true; show(event); },
      // Mouse focus from a click must not leave the preview stuck open.
      onMouseLeave: event => { children.props.onMouseLeave?.(event); hovered.current = false; setOpen(false); },
      onFocus: event => { children.props.onFocus?.(event); show(event); },
      onBlur: event => {
        children.props.onBlur?.(event);
        if (event.currentTarget.contains(event.relatedTarget)) return;
        if (!hovered.current) setOpen(false);
      },
    })}
    {open && createPortal(
      <div ref={popup} id={id} role="tooltip" aria-label={`${title} details`}
        data-movie-preview={movieId}
        className="pointer-events-none fixed z-[1000] w-[620px] max-w-[calc(100vw-24px)] max-h-[calc(100dvh-24px)] overflow-y-auto rounded-2xl border border-white/20 bg-[#140f20] p-4 text-white shadow-2xl"
        style={{ ...position, visibility: position ? "visible" : "hidden" }}>
        <div className="grid grid-cols-1 items-start gap-4 sm:grid-cols-2">
          <div ref={poster} className="mx-auto w-full" style={{ maxWidth: ((position?.width || 620) - 50) / 2 }}>
            <MoviePoster movieId={movieId} title={title} urls={previewUrls} />
          </div>
          <div className="min-w-0">
            <p className="text-lg font-semibold leading-snug">{title}</p>
            {(year || rating) && <p className="mt-2 text-xs text-gray-300">
              {year}{year && rating ? " · " : ""}{rating ? <span className="text-[#FFF2B3]">★ {rating}</span> : ""}
            </p>}
            {genres.length > 0 && <p className="mt-2 text-xs text-gray-400">{genres.join(" · ")}</p>}
            <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-gray-200">
              {synopsis || "Synopsis unavailable."}
            </p>
          </div>
        </div>
      </div>, document.body
    )}
  </>;
}
